import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { clsx } from 'clsx';

export interface PeriodInput {
  start: string;
  end: string;
  label: string;
}

export interface PeriodFieldError {
  start?: string;
  end?: string;
  label?: string;
}

interface PeriodComparisonPickerProps {
  periods: PeriodInput[];
  onChange: (periods: PeriodInput[]) => void;
  onApplyPreset: (preset: 'currentVsPrevious' | 'yearOverYear') => void;
  errors?: PeriodFieldError[];
}

export default function PeriodComparisonPicker({
  periods,
  onChange,
  onApplyPreset,
  errors,
}: PeriodComparisonPickerProps) {
  const { t } = useTranslation('search');

  const updatePeriod = (idx: number, patch: Partial<PeriodInput>) => {
    const next = [...periods];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const addPeriod = () => {
    if (periods.length >= 3) return;
    const last = periods[periods.length - 1];
    onChange([...periods, { start: last.start, end: last.end, label: t('analytics.periodN', { n: periods.length + 1 }) }]);
  };

  const removePeriod = (idx: number) => {
    if (periods.length <= 2) return;
    const next = periods.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-700">{t('analytics.periodsTitle')}</h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onApplyPreset('currentVsPrevious')}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
          >
            {t('analytics.presets.currentVsPrevious')}
          </button>
          <button
            type="button"
            onClick={() => onApplyPreset('yearOverYear')}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
          >
            {t('analytics.presets.yearOverYear')}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {periods.map((period, idx) => {
          const err = errors?.[idx] ?? {};
          const hasError = !!(err.label || err.start || err.end);
          return (
            <div
              key={idx}
              className={clsx(
                'rounded-lg border p-3 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-start',
                hasError ? 'border-rose-300 bg-rose-50/40' : 'border-gray-200',
              )}
            >
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('analytics.periodLabel')}
                </label>
                <input
                  type="text"
                  value={period.label}
                  onChange={(e) => updatePeriod(idx, { label: e.target.value })}
                  aria-invalid={!!err.label}
                  className={clsx(
                    'w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2',
                    err.label
                      ? 'border-rose-400 focus:ring-rose-500 bg-white'
                      : 'border-gray-300 focus:ring-primary-500',
                  )}
                />
                {err.label && <p className="mt-1 text-xs text-rose-600">{err.label}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('analytics.periodStart')}
                </label>
                <input
                  type="date"
                  value={period.start}
                  onChange={(e) => updatePeriod(idx, { start: e.target.value })}
                  aria-invalid={!!err.start}
                  className={clsx(
                    'w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2',
                    err.start
                      ? 'border-rose-400 focus:ring-rose-500 bg-white'
                      : 'border-gray-300 focus:ring-primary-500',
                  )}
                />
                {err.start && <p className="mt-1 text-xs text-rose-600">{err.start}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('analytics.periodEnd')}
                </label>
                <input
                  type="date"
                  value={period.end}
                  onChange={(e) => updatePeriod(idx, { end: e.target.value })}
                  aria-invalid={!!err.end}
                  className={clsx(
                    'w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2',
                    err.end
                      ? 'border-rose-400 focus:ring-rose-500 bg-white'
                      : 'border-gray-300 focus:ring-primary-500',
                  )}
                />
                {err.end && <p className="mt-1 text-xs text-rose-600">{err.end}</p>}
              </div>
              <button
                type="button"
                onClick={() => removePeriod(idx)}
                disabled={periods.length <= 2}
                className="self-end p-2 rounded-lg text-rose-500 hover:bg-rose-50 disabled:opacity-30 disabled:cursor-not-allowed"
                title={t('analytics.removePeriod')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      {periods.length < 3 && (
        <button
          type="button"
          onClick={addPeriod}
          className="flex items-center gap-2 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg"
        >
          <Plus className="w-4 h-4" />
          {t('analytics.addPeriod')}
        </button>
      )}
    </div>
  );
}
